import { useState, useEffect, useRef, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import Dialog from '@mui/material/Dialog'
import Box from '@mui/material/Box'
import InputBase from '@mui/material/InputBase'
import Typography from '@mui/material/Typography'
import { BlynIcon } from './BlynIcon'
import { store } from './legacy/state/store'
import { listProjects, type BlynProject } from './services/projectsService'
import { listConversations, type BlynConversation } from './services/conversationsService'

function usePaletteOpen() {
  const [open, setOpen] = useState(false)
  useEffect(() => {
    setOpen((store.get('paletteOpen') as boolean) ?? false)
    const unsub = store.select('paletteOpen', (v: boolean) => setOpen(!!v))
    return () => unsub?.()
  }, [])
  return open
}

const NAV_ITEMS = [
  { id: 'nav:new-conversation',    label: 'New conversation',     view: 'new-conversation',    icon: 'plus' },
  { id: 'nav:projects',            label: 'Projects',             view: 'projects',            icon: 'folder' },
  { id: 'nav:tasks',               label: 'Running tasks',        view: 'tasks',               icon: 'zap' },
  { id: 'nav:creatives',           label: 'Creatives',            view: 'creatives',           icon: 'mail' },
  { id: 'nav:web-personalization', label: 'Web personalization',  view: 'web-personalization', icon: 'globe' },
  { id: 'nav:product-catalog',     label: 'Product catalog',      view: 'product-catalog',     icon: 'package' },
  { id: 'nav:knowledge',           label: 'Knowledge base',       view: 'knowledge',           icon: 'book' },
  { id: 'nav:forecast',            label: 'Insights & forecasts', view: 'forecast',            icon: 'trending' },
  { id: 'nav:staged',              label: 'Staged actions',       view: 'staged',              icon: 'staged' },
  { id: 'nav:approvals',           label: 'Approvals queue',      view: 'approvals',           icon: 'approvals' },
  { id: 'nav:nudges',              label: 'Nudges',               view: 'nudges',              icon: 'bell' },
  { id: 'nav:notifications',       label: 'Notifications',        view: 'notifications',       icon: 'notifications' },
  { id: 'nav:settings',            label: 'Settings',             view: 'settings',            icon: 'settings' },
]

interface PaletteItem {
  id: string
  label: string
  meta?: string
  icon?: string
  section: string
  onSelect: () => void
}

function fuzzyMatch(query: string, text: string): boolean {
  if (!query) return true
  const q = query.toLowerCase()
  const t = text.toLowerCase()
  let qi = 0
  for (let i = 0; i < t.length && qi < q.length; i++) {
    if (t[i] === q[qi]) qi++
  }
  return qi === q.length
}

export default function CommandPalette() {
  const navigate = useNavigate()
  const open = usePaletteOpen()
  const [query, setQuery] = useState('')
  const [projects, setProjects] = useState<BlynProject[]>([])
  const [conversations, setConversations] = useState<BlynConversation[]>([])
  const [selectedIdx, setSelectedIdx] = useState(0)
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      const isInput = (e.target as HTMLElement)?.closest('input, textarea, [contenteditable="true"]');
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault()
        store.set({ paletteOpen: !store.get('paletteOpen') })
      } else if (e.key === '?' && !e.metaKey && !e.ctrlKey && !isInput) {
        e.preventDefault()
        const currentView = (store.get('currentView') as string) || 'conversation'
        store.set({ helpOpen: true, helpViewId: currentView })
      }
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [])

  useEffect(() => {
    if (!open) { setQuery(''); setSelectedIdx(0); return }
    setTimeout(() => inputRef.current?.focus(), 50)
    listProjects().then(setProjects).catch(() => {})
    listConversations().then(setConversations).catch(() => {})
  }, [open])

  useEffect(() => { setSelectedIdx(0) }, [query])

  function close() { store.set({ paletteOpen: false }) }

  function goTo(view: string) {
    close()
    navigate(`/blyn/${view}`)
  }

  const allItems: PaletteItem[] = [
    ...NAV_ITEMS
      .filter(i => fuzzyMatch(query, i.label))
      .map(i => ({
        id: i.id, label: i.label, icon: i.icon, section: 'Go to',
        onSelect: () => goTo(i.view),
      })),
    ...projects
      .filter(p => fuzzyMatch(query, p.name))
      .slice(0, 6)
      .map(p => ({
        id: `project:${p.id}`, label: p.name, meta: p.statusLabel, section: 'Projects',
        onSelect: () => { store.set({ currentProjectId: p.id }); goTo('projects') },
      })),
    ...conversations
      .filter(c => fuzzyMatch(query, c.title || c.preview || ''))
      .slice(0, 6)
      .map(c => ({
        id: `conv:${c.id}`,
        label: c.title || c.preview || 'Untitled',
        meta: c.updatedAt,
        section: 'Conversations',
        onSelect: () => {
          store.set({ currentConversationId: c.id })
          close()
          navigate(`/blyn/new-conversation?id=${c.id}`)
        },
      })),
  ]

  function handleKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'ArrowDown') { e.preventDefault(); setSelectedIdx(i => Math.min(i + 1, allItems.length - 1)) }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setSelectedIdx(i => Math.max(i - 1, 0)) }
    else if (e.key === 'Enter') { e.preventDefault(); allItems[selectedIdx]?.onSelect() }
  }

  const sections: Record<string, PaletteItem[]> = {}
  for (const item of allItems) {
    if (!sections[item.section]) sections[item.section] = []
    sections[item.section].push(item)
  }

  let globalIdx = 0

  return (
    <Dialog
      open={open}
      onClose={close}
      PaperProps={{
        sx: {
          bgcolor: 'var(--bg-canvas)',
          backgroundImage: 'none',
          border: '1px solid var(--line-strong)',
          borderRadius: '12px',
          width: '100%',
          maxWidth: 560,
          m: 0,
          overflow: 'hidden',
          boxShadow: '0 24px 64px rgba(0,0,0,0.4)',
        },
      }}
      sx={{ '& .MuiDialog-container': { alignItems: 'flex-start', pt: '14vh' } }}
      BackdropProps={{ sx: { bgcolor: 'rgba(0,0,0,0.45)' } }}
    >
      {/* Input */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 2, py: 1.5, borderBottom: '1px solid var(--line)' }}>
        <Box sx={{ color: 'var(--text-primary)', opacity: 0.5, display: 'flex', flexShrink: 0 }}>
          <BlynIcon name="search" size={15} />
        </Box>
        <InputBase
          inputRef={inputRef}
          fullWidth
          placeholder="Search projects, conversations, commands…"
          value={query}
          onChange={e => setQuery(e.target.value)}
          onKeyDown={handleKeyDown}
          sx={{
            fontSize: 14,
            color: 'var(--text-primary)',
            '& input::placeholder': { color: 'var(--text-primary)', opacity: 0.4 },
          }}
        />
        {query && (
          <Box
            component="button"
            onClick={() => setQuery('')}
            sx={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-primary)', opacity: 0.4, p: 0, display: 'flex', '&:hover': { opacity: 0.8 } }}
          >
            <BlynIcon name="close" size={14} />
          </Box>
        )}
      </Box>

      {/* Results */}
      <Box sx={{ maxHeight: '50vh', overflowY: 'auto', py: 0.5 }}>
        {allItems.length === 0 ? (
          <Box sx={{ px: 3, py: 4, textAlign: 'center', color: 'var(--text-primary)', opacity: 0.4, fontSize: 13 }}>
            No results for "{query}"
          </Box>
        ) : (
          Object.entries(sections).map(([section, items]) => (
            <Box key={section}>
              <Typography sx={{
                px: 2, pt: 1.5, pb: 0.5,
                fontSize: 10, fontWeight: 600,
                letterSpacing: '0.08em', textTransform: 'uppercase',
                color: 'var(--text-primary)', opacity: 0.4,
                fontFamily: 'var(--font-mono)',
              }}>
                {section}
              </Typography>
              {items.map(item => {
                const idx = globalIdx++
                const isSelected = idx === selectedIdx
                return (
                  <Box
                    key={item.id}
                    onClick={item.onSelect}
                    onMouseEnter={() => setSelectedIdx(idx)}
                    sx={{
                      display: 'flex', alignItems: 'center', gap: 1.5,
                      px: 2, py: 0.875,
                      cursor: 'pointer',
                      bgcolor: isSelected ? 'var(--bg-hover)' : 'transparent',
                      transition: 'background var(--t-fast)',
                    }}
                  >
                    {item.icon && (
                      <Box sx={{
                        width: 28, height: 28, display: 'grid', placeItems: 'center',
                        borderRadius: 'var(--r-sm)',
                        bgcolor: 'var(--bg-app)',
                        border: '1px solid var(--line)',
                        flexShrink: 0,
                        color: 'var(--text-primary)',
                      }}>
                        <BlynIcon name={item.icon} size={14} />
                      </Box>
                    )}
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography sx={{ fontSize: 13, color: 'var(--text-primary)', lineHeight: 1.4 }} noWrap>
                        {item.label}
                      </Typography>
                      {item.meta && (
                        <Typography sx={{ fontSize: 11, color: 'var(--text-primary)', opacity: 0.45, lineHeight: 1.3 }}>
                          {item.meta}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                )
              })}
            </Box>
          ))
        )}
      </Box>

      {/* Footer */}
      <Box sx={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        px: 2, py: 1,
        borderTop: '1px solid var(--line)',
        color: 'var(--text-primary)', opacity: 0.45, fontSize: 11,
      }}>
        <Box sx={{ display: 'flex', gap: 2, fontSize: 11 }}>
          <span>↑↓ navigate</span>
          <span>⏎ select</span>
          <span>ESC close</span>
        </Box>
        <Box sx={{
          px: '5px', py: '2px', borderRadius: '4px',
          border: '1px solid var(--line)',
          fontFamily: 'var(--font-mono)', fontSize: 10,
        }}>⌘K</Box>
      </Box>
    </Dialog>
  )
}
